import {
  TAuthConverter,
  TCreateConverter,
  TFetchConverter,
} from 'data/types/converter.types';
import {
  TAgeGroupOptionsByLanguageUI,
  TClassDB,
  TClassUI,
  TEventChildcareRequirementsDB,
  TEventChildcareRequirementsUI,
  TEventDB,
  TClassesByAgeGroupUI,
  TClassesByAgeGroupDB,
  TKitShippingAddressEditUI,
  TKitShippingAddressEditDB,
  TSubscribeDataUI,
  TSubscribeDataDB,
  TEventRegisterDataDB,
  TEventRegisterDataUI,
  TEventUI,
  TEventDetailUI,
  TEventDetailDB,
  TClassesUi,
  TClassesDB,
  TSessionsUi,
  TSessionsDB,
  TLanguagesStatusUi,
  TLanguageStatusItemUi,
  TPartInfoUi,
  TPartInfoDB,
  TEventRegisterResponseUI,
  TEventRegisterResponseDB,
  TRegistrationFormDataDB,
  TRegistrationFormDataUI,
  TClassesGroupedByAgeUI,
  TLocationCoordinatesUI,
  TLocationCoordinatesDB,
  EEventAgeGroup,
  EEventPartType,
  TEventEventsUI,
  TEventEventsDB,
  EEventType,
  TLightBoxUI,
  TLightBoxDB,
  TPartsUI,
  TPartsDB,
  TEventMultipleRegisterDataUI,
  TEventMultipleRegisterDataDB,
  TEventMultipleRegisterResponseUI,
  TEventMultipleRegisterResponseDB,
} from 'data/types/events.types';
import { ELanguage } from 'data/types/generic.types';
import {
  dateConverter,
  dateFormatWithWeekdayAndTime,
  dateFormatWithYearWeekdayAndTime,
  dateTimeFormatUI,
  dateTimeWithoutSecUI,
  groupAdjacentAges,
  separateDateAndTime,
} from 'helpers/utils';
import {
  addressConvertor,
  i18nLanguageConverter,
  phoneNumberConverter,
  yesNoOptionConverter,
} from './general.converters';

export const eventChildcareRequirementsConverter: TCreateConverter<
  TEventChildcareRequirementsUI,
  TEventChildcareRequirementsDB
> = {
  toDb: data => {
    return {
      ageGroup: data.name,
      childCount: data.value,
    };
  },
};

export const registerEventConverter: TAuthConverter<
  TEventRegisterDataUI,
  TEventRegisterDataDB,
  TEventRegisterResponseUI,
  TEventRegisterResponseDB
> = {
  toDb: data => {
    const payload: TEventRegisterDataDB = {
      hasReadyBinder: yesNoOptionConverter.toDb(data?.hasReadyBinder),
      eventId: data.eventId,
      eventClassId: data.eventClassId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: phoneNumberConverter.toDb(data.phone) || '',
      childNames: (data.childNames || [])
        .map(child => child.name)
        .filter(item => item),
      childcareRequirements: (data.childcareRequirements || []).reduce(
        (acc: TEventChildcareRequirementsDB[], curr) =>
          curr.checked
            ? [...acc, eventChildcareRequirementsConverter.toDb(curr)]
            : acc,
        [],
      ),
      recaptchaResponse: data.recaptchaResponse,
      userLanguage:
        data.userLanguage && i18nLanguageConverter.toDb(data.userLanguage),
      materialsLanguage: data.materialsLanguage,
      allowedTakePicture: yesNoOptionConverter.toDb(data.allowedTakePicture),
      acceptedTerms: data.acceptedTerms,
      notificationDisabled: !!data.notificationDisabled,
    };

    if (data.customQuestionAnswer) {
      payload.customQuestionAnswer = data.customQuestionAnswer;
    }

    if (data.additionalAdults) {
      payload.additionalAdults = data.additionalAdults.map(item => ({
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: phoneNumberConverter.toDb(item.phone) || '',
      }));
    }

    return payload;
  },
  fromDb: data => {
    return {
      status: data.status,
      registrantId: data.registrantId,
    };
  },
};

export const registerMultipleEventConverter: TAuthConverter<
  TEventMultipleRegisterDataUI,
  TEventMultipleRegisterDataDB,
  TEventMultipleRegisterResponseUI,
  TEventMultipleRegisterResponseDB
> = {
  toDb: data => {
    return {
      registrationDetails: data.registrationDetails.map(item =>
        registerEventConverter.toDb(item),
      ),
    };
  },
  fromDb: data => {
    // A successful registration must never fail client-side on an unexpected
    // response body (e.g. an empty 200/204), so tolerate non-array payloads.
    if (!Array.isArray(data)) return [];
    return data.map(item => registerEventConverter.fromDb(item));
  },
};

export const partsConverter: TFetchConverter<TPartsUI, TPartsDB> = {
  fromDb: data => {
    return {
      partType: data.partType,
      date: separateDateAndTime(data.dateTime),
      dateTimeString:
        data.dateTime && dateConverter(data.dateTime, dateTimeFormatUI),
      isPassed: data.isPassed,
    };
  },
};
export const eventClassConverter: TFetchConverter<TClassUI, TClassDB> = {
  fromDb: data => {
    return {
      classId: data.eventClassId,
      date:
        (data.sessions[0].dateTime &&
          dateConverter(data.sessions[0].dateTime, dateTimeFormatUI)) ||
        '',
      ageGroup: data.ageGroup,
      language: data.language,
      sessions: data.sessions?.map(item => partsConverter.fromDb(item)) || [],
      isAllSessionsPassed: data.sessions?.every(item => item.isPassed),
    };
  },
};
export const classesByAgeGroupConverter: TFetchConverter<
  TClassesByAgeGroupUI[],
  TClassesByAgeGroupDB
> = {
  fromDb: data => {
    //  groups order is used to sort age groupes in UI, add new item in correct place
    const groups = [
      EEventAgeGroup.ZERO_TO_ONE,
      EEventAgeGroup.ONE_TO_TWO,
      EEventAgeGroup.TWO_TO_THREE,
      EEventAgeGroup.TWO_TO_FIVE,
      EEventAgeGroup.THREE_TO_FOUR,
      EEventAgeGroup.THREE_TO_FIVE,
      EEventAgeGroup.FOUR_TO_FIVE,
    ];
    const existingGroups = groups.filter(key => data[key]);
    const objectKeyValues: TClassesByAgeGroupUI[] = existingGroups.map(key => {
      const convertedKey = key as EEventAgeGroup;
      const convertedValue: TClassUI = eventClassConverter.fromDb(data[key]);
      return [convertedKey, convertedValue] as TClassesByAgeGroupUI;
    });
    return objectKeyValues;
  },
};

const getLanguageOptions = (
  englishClassesLength: number,
  spanishClassesLength: number,
) => {
  const classes = [];
  if (englishClassesLength) {
    classes.push({
      value: ELanguage.ENGLISH,
      label: ELanguage.ENGLISH,
    });
  }
  if (spanishClassesLength) {
    classes.push({
      value: ELanguage.SPANISH,
      label: ELanguage.SPANISH,
    });
  }
  return classes;
};
const getAgeGroupOptionsByLanguage = (
  englishClasses: TClassesByAgeGroupDB | null,
  spanishClasses: TClassesByAgeGroupDB | null,
) => {
  const ageGroupsByLanguage: TAgeGroupOptionsByLanguageUI = {
    [ELanguage.SPANISH]: [],
    [ELanguage.ENGLISH]: [],
  };
  if (Object.keys(englishClasses || {}).length) {
    ageGroupsByLanguage[ELanguage.ENGLISH] = Object.keys(
      englishClasses || {},
    ).map(item => {
      const convertedKey = item as EEventAgeGroup;
      return { value: convertedKey, label: convertedKey };
    });
  }
  if (Object.keys(spanishClasses || {}).length) {
    ageGroupsByLanguage[ELanguage.SPANISH] = Object.keys(
      spanishClasses || {},
    ).map(item => {
      const convertedKey = item as EEventAgeGroup;
      return { value: convertedKey, label: convertedKey };
    });
  }
  return ageGroupsByLanguage;
};

export const locationCoordinatesConverter: TFetchConverter<
  TLocationCoordinatesUI,
  TLocationCoordinatesDB
> = {
  fromDb: data => {
    return {
      latitude: data.latitude,
      longitude: data.longitude,
    };
  },
};

export const sessionsConverter: TFetchConverter<TSessionsUi, TSessionsDB> = {
  fromDb: data => {
    return {
      dateTime:
        data.dateTime &&
        dateConverter(data.dateTime, dateFormatWithYearWeekdayAndTime),
      isPassed: data.isPassed,
      partType: data.partType,
    };
  },
};

export const classConverter: TFetchConverter<TClassesUi, TClassesDB> = {
  fromDb: data => {
    return {
      ageGroup: data.ageGroup,
      language: data.language,
      eventClassId: data.eventClassId,
      sessions: data.sessions.map(item => sessionsConverter.fromDb(item)),
    };
  },
};

export const sortByLanguageConverter: TFetchConverter<
  TClassesUi[],
  TClassesDB[]
> = {
  fromDb: data => {
    const englishClasses: TClassesUi[] = [];
    const spanishClasses: TClassesUi[] = [];

    data.forEach(item => {
      if (item.language === ELanguage.ENGLISH) {
        englishClasses.push(classConverter.fromDb(item) || []);
      } else if (item.language === ELanguage.SPANISH) {
        spanishClasses.push(classConverter.fromDb(item) || []);
      }
    });

    return [...englishClasses, ...spanishClasses].filter(
      item => Object.keys(item).length > 0,
    );
  },
};

export const classesGroupedByAgeConverter: TFetchConverter<
  TClassesGroupedByAgeUI[],
  TClassesDB[]
> = {
  fromDb: data => {
    const ageGroups = [
      EEventAgeGroup.ZERO_TO_ONE,
      EEventAgeGroup.ONE_TO_TWO,
      EEventAgeGroup.TWO_TO_THREE,
      EEventAgeGroup.TWO_TO_FIVE,
      EEventAgeGroup.THREE_TO_FOUR,
      EEventAgeGroup.FOUR_TO_FIVE,
      EEventAgeGroup.THREE_TO_FIVE,
    ];

    return ageGroups
      .map(ageGroup => {
        const classesForAgeGroup = data
          .filter(item => item.ageGroup === ageGroup)
          .map(item => item);

        return {
          ...(classesForAgeGroup.length && {
            ageGroup,
            classes: sortByLanguageConverter.fromDb(classesForAgeGroup),
          }),
        };
      })
      .filter(item => Object.keys(item).length > 0);
  },
};

export const eventConverter: TFetchConverter<TEventDetailUI, TEventDetailDB> = {
  fromDb: data => {
    const allClasses = data?.classes || [];
    const isAllClassesFinished = allClasses.length
      ? allClasses.every(item =>
          item.sessions.every(session => session.isPassed),
        )
      : false;
    return {
      childcareProvided: data.childcareProvided,
      classes: classesGroupedByAgeConverter.fromDb(allClasses),
      creationDate: data.creationDate,
      foodProvided: data.foodProvided,
      hostId: data.hostId,
      hostName: data.hostName,
      locationAddress: data.locationAddress,
      locationCoordinates:
        data.locationCoordinates &&
        locationCoordinatesConverter.fromDb(data.locationCoordinates),
      locationAddressId: data.locationAddressId,
      locationName: data.locationName,
      eventCategory: data.eventCategory,
      eventDescription: data.eventDescription,
      eventId: data.eventId,
      eventPhoto: data.eventPhoto,
      eventType: data.eventType,
      eventTitle: data.eventTitle,
      isAllClassesFinished,
      attendanceType: data.attendanceType,
    };
  },
};

export const languageStatusItemConverter: TFetchConverter<
  TLanguagesStatusUi,
  TLanguageStatusItemUi
> = {
  fromDb: data => {
    return {
      language: data.language,
      isPassed: data.sessions.every(item => item.isPassed),
    };
  },
};

export const partsInfoConverter: TFetchConverter<TPartInfoUi, TPartInfoDB> = {
  fromDb: data => {
    const partDatesGrouped = Array.from(
      data.parts.map(item => item.dateTime),
    ).flatMap(innerArray => innerArray);
    // Parse the date strings and format them without seconds
    const formattedDates = partDatesGrouped.map(dateString =>
      dateConverter(dateString, dateTimeWithoutSecUI),
    );
    // Check if all formatted dates are the same
    const allDatesAreSame = formattedDates.every(
      date => date === formattedDates[0],
    );
    return {
      partType: data.partType,
      isPassed: data.parts.every(item => item.isPassed),
      dateTime: allDatesAreSame
        ? dateConverter(partDatesGrouped[0], dateFormatWithWeekdayAndTime)
        : null,
    };
  },
};

export const languageStatusConverter: TFetchConverter<
  TLanguagesStatusUi[],
  TClassesUi[]
> = {
  fromDb: data => {
    const languagesArr: TLanguagesStatusUi[] = [];

    const languageData = (sessions: TSessionsUi[], language: ELanguage) => {
      if (sessions.length) {
        const convertedPart = languageStatusItemConverter.fromDb({
          sessions,
          language,
        });
        if (!convertedPart.isPassed) {
          languagesArr.push(convertedPart);
        }
      }
    };

    const englishSessions = data
      .filter(item => item.language === ELanguage.ENGLISH)
      .flatMap(item => item.sessions);
    const spanishSessions = data
      .filter(item => item.language === ELanguage.SPANISH)
      .flatMap(item => item.sessions);

    languageData(englishSessions, ELanguage.ENGLISH);
    languageData(spanishSessions, ELanguage.SPANISH);
    return languagesArr;
  },
};

export const sessionsGroupedConverter: TFetchConverter<
  TPartInfoUi[],
  TEventEventsDB
> = {
  fromDb: data => {
    const partsArr: TPartInfoUi[] = [];

    const partTypes = [
      EEventPartType.PART_ONE,
      EEventPartType.PART_TWO,
      EEventPartType.PART_THREE,
      EEventPartType.STANDALONE,
    ];

    partTypes.forEach(partType => {
      const partArray = data.classes
        .flatMap(item => item.sessions)
        .filter(item => item.partType === partType)
        .flatMap(innerArray => innerArray);

      if (partArray.length) {
        const convertedPart = partsInfoConverter.fromDb({
          partType,
          parts: partArray,
        });
        partsArr.push(convertedPart);
      }
    });
    return partsArr;
  },
};

export const eventEventsConverter: TFetchConverter<
  TEventEventsUI[],
  TEventEventsDB[]
> = {
  fromDb: events => {
    return (
      events?.map(data => {
        // Group the age groups
        const eventAgeGroups = Array.from(
          new Set(data.classes.map(item => item.ageGroup)),
        );
        // Grouped all classes in one array
        const eventClassesGrouped = Array.from(data.classes.map(item => item))
          .flatMap(innerArray => innerArray)
          .map(item => classConverter.fromDb(item));
        return {
          classes: (data.classes || []).map(item =>
            classConverter.fromDb(item),
          ),
          sessionsGrouped: sessionsGroupedConverter.fromDb(data),
          ageGroups: groupAdjacentAges(eventAgeGroups),
          languagesStatus: languageStatusConverter.fromDb(eventClassesGrouped),
          childcareProvided: data.childcareProvided,
          creationDate: data.creationDate,
          foodProvided: data.foodProvided,
          locationAddress: data.locationAddress,
          locationName: data.locationName,
          eventCategory: data.eventCategory,
          eventId: data.eventId,
          eventPhoto: data.eventPhoto,
          eventType: data.eventType,
          attendanceType: data.attendanceType,
        };
      }) || []
    );
  },
};

export const eventsConverter: TFetchConverter<TEventUI, TEventDB> = {
  fromDb: data => {
    return {
      hostName: data.hostName,
      eventEvents: eventEventsConverter.fromDb(data.eventEvents),
    };
  },
};

export const kitOrderingShippingAddressConverter: TCreateConverter<
  TKitShippingAddressEditUI,
  TKitShippingAddressEditDB
> = {
  toDb: data => ({
    parentOrderId: data.parentOrderId,
    address: addressConvertor.toDb(data.address),
  }),
};

export const notifyMeConverter: TCreateConverter<
  TSubscribeDataUI,
  TSubscribeDataDB
> = {
  toDb: data => {
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: phoneNumberConverter.toDb(data.phone) || '',
      recaptchaResponse: data.recaptchaResponse,
      cities: data.cities,
      ageGroups: (data.ageGroups || []).map(ageGroup => ageGroup),
      languages: (data.languages || []).map(language => language),
    };
  },
};

export const registrationFormDataConverter: TFetchConverter<
  TRegistrationFormDataUI,
  TRegistrationFormDataDB | TEventDetailDB
> = {
  fromDb: data => {
    // Handle legacy registration/form response
    if ((data as TRegistrationFormDataDB).englishClasses !== undefined) {
      const regData = data as TRegistrationFormDataDB;
      const spanishClasses = regData.spanishClasses
        ? classesByAgeGroupConverter.fromDb(regData.spanishClasses)
        : [];
      const englishClasses = regData.englishClasses
        ? classesByAgeGroupConverter.fromDb(regData.englishClasses)
        : [];

      const isCCP = [
        EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
        EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
        EEventType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
      ].includes(regData.eventType);

      return {
        childcareProvided: regData.childcareProvided,
        foodProvided: regData.foodProvided,
        hostId: regData.hostId,
        hostName: regData.hostName,
        eventId: regData.eventId,
        eventType: regData.eventType,
        eventCategory: regData.eventCategory,
        eventTitle: regData.eventTitle,
        spanishClasses,
        englishClasses,
        languageOptions: getLanguageOptions(
          Object.keys(regData.englishClasses || {}).length,
          Object.keys(regData.spanishClasses || {}).length,
        ),
        ageGroupOptions: getAgeGroupOptionsByLanguage(
          regData.englishClasses,
          regData.spanishClasses,
        ),
        parts: regData.parts?.map(item => partsConverter.fromDb(item)) || [],
        customQuestion: regData.customQuestion,
        isCCP,
        locationName: regData.locationName,
        locationAddress: regData.locationAddress,
        // non-typed field from backend; used to display language when no classes are present
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventLanguage: (regData as any)?.eventLanguage,
      };
    }

    // Handle lightweight registration/form response that contains top-level parts
    // but no english/spanish classes (e.g., symposium or simplified forms)
    // Example payload shape (from backend):
    // { parts: [{ partType, startDateTime, endDateTime, isPassed }], ... }
    if (
      (data as any).parts !== undefined &&
      (data as any).classes === undefined
    ) {
      const regData = data as any;
      const rawParts = Array.isArray(regData.parts) ? regData.parts : [];
      // Map start/end datetime into the expected dateTime field for downstream converters
      const partsDb: TPartsDB[] = rawParts.map((p: any) => ({
        partType: p.partType,
        isPassed: !!p.isPassed,
        // Prefer startDateTime; fall back to endDateTime
        dateTime: p.startDateTime || p.endDateTime || null,
      }));

      const isCCP = [
        EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
        EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
        EEventType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
      ].includes(regData.eventType as EEventType);

      return {
        childcareProvided: !!regData.childcareProvided,
        foodProvided: regData.foodProvided,
        hostId: regData.hostId,
        hostName: regData.hostName,
        eventId: regData.eventId,
        eventType: regData.eventType,
        eventCategory: regData.eventCategory,
        eventTitle: regData.eventTitle,
        spanishClasses: [],
        englishClasses: [],
        languageOptions: [],
        ageGroupOptions: {
          [ELanguage.ENGLISH]: [],
          [ELanguage.SPANISH]: [],
        },
        parts: partsDb
          .filter(p => !!p.dateTime)
          .map(item => partsConverter.fromDb(item as TPartsDB)),
        customQuestion: regData.customQuestion,
        isCCP,
        locationName: regData.locationName,
        locationAddress: regData.locationAddress,
        // Keep compatibility with consumers expecting this optional field
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        eventLanguage: (regData as any)?.eventLanguage,
      };
    }

    // Handle new event-event/{id} response
    const eventDetail = data as TEventDetailDB;

    // Build english/spanish maps by age group from classes list
    const englishClassesMap: Partial<TClassesByAgeGroupDB> = {};
    const spanishClassesMap: Partial<TClassesByAgeGroupDB> = {};
    (eventDetail.classes || []).forEach((cls: TClassesDB) => {
      const targetMap =
        cls.language === ELanguage.ENGLISH
          ? (englishClassesMap as any)
          : (spanishClassesMap as any);
      // choose the first class per age group if multiple
      if (!targetMap[cls.ageGroup]) {
        targetMap[cls.ageGroup] = {
          ageGroup: cls.ageGroup,
          language: cls.language,
          sessions: cls.sessions,
          eventClassId: cls.eventClassId,
        } as TClassDB;
      }
    });

    const englishClasses = classesByAgeGroupConverter.fromDb(
      englishClassesMap as TClassesByAgeGroupDB,
    );
    const spanishClasses = classesByAgeGroupConverter.fromDb(
      spanishClassesMap as TClassesByAgeGroupDB,
    );

    const isCCP = [
      EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
      EEventType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
      EEventType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
    ].includes(eventDetail.eventType);

    // Flatten sessions to build parts
    const partsDb: TPartsDB[] = (eventDetail.classes || [])
      .flatMap(c => c.sessions)
      .map(s => ({
        dateTime: s.dateTime,
        isPassed: s.isPassed,
        partType: s.partType,
      }));

    return {
      childcareProvided: eventDetail.childcareProvided,
      foodProvided: eventDetail.foodProvided,
      hostId: eventDetail.hostId,
      hostName: eventDetail.hostName,
      eventId: eventDetail.eventId,
      eventType: eventDetail.eventType,
      eventCategory: eventDetail.eventCategory,
      eventTitle: eventDetail.eventTitle,
      spanishClasses,
      englishClasses,
      languageOptions: getLanguageOptions(
        englishClasses.length,
        spanishClasses.length,
      ),
      ageGroupOptions: getAgeGroupOptionsByLanguage(
        englishClassesMap as TClassesByAgeGroupDB,
        spanishClassesMap as TClassesByAgeGroupDB,
      ),
      parts: partsDb.map(item => partsConverter.fromDb(item)),
      isCCP,
      locationName: eventDetail.locationName,
      locationAddress: eventDetail.locationAddress,
      eventLanguage: (eventDetail as any)?.eventLanguage,
    };
  },
};

export const lightBoxConverter: TFetchConverter<TLightBoxUI, TLightBoxDB> = {
  fromDb: data => {
    return {
      id: data.id,
      firstSection: data.firstSection,
      secondSection: data.secondSection,
      picture: data.picture,
      published: data.published,
    };
  },
};
